import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { GLOSSARY } from "@/constants/testIds";
import { SensitivityBadge } from "@/components/GlossaryBadges";
import api from "@/lib/api";

const LIFECYCLES = ["Draft", "Active", "Deprecated"];
const SENSITIVITIES = ["Public", "Internal", "Confidential", "Restricted"];

export function TermDrawer({ term, sampleValues, dataType, onOpenChange, onSaved }) {
  const [definition, setDefinition] = useState("");
  const [lifecycle, setLifecycle] = useState("Draft");
  const [sensitivity, setSensitivity] = useState("Internal");
  const [synonyms, setSynonyms] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (term) {
      setDefinition(term.definition || "");
      setLifecycle(term.lifecycle || "Draft");
      setSensitivity(term.sensitivity || "Internal");
      setSynonyms((term.synonyms || []).join(", "));
    }
  }, [term]);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        definition,
        lifecycle,
        sensitivity,
        synonyms: synonyms.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.patch(`/glossary/${term.id}`, payload);
      toast.success("Definition saved & re-embedded");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={!!term} onOpenChange={(o) => !o && onOpenChange(false)}>
      <SheetContent
        data-testid={GLOSSARY.drawer}
        side="right"
        className="w-full overflow-y-auto font-mono sm:max-w-lg"
      >
        {term && (
          <>
            <SheetHeader className="space-y-2 text-left">
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {term.kind}
                </span>
                <SensitivityBadge value={sensitivity} />
              </div>
              <SheetTitle className="text-xl">{term.display_name}</SheetTitle>
              <SheetDescription className="font-mono text-xs">
                {term.table_fqn}
                {term.column_name ? ` · ${term.column_name}` : ""}
                {dataType ? ` · ${dataType}` : ""}
              </SheetDescription>
            </SheetHeader>

            {sampleValues && sampleValues.length > 0 && (
              <div className="mt-6">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Sample values
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {sampleValues.map((v, i) => (
                    <span
                      key={i}
                      className="rounded-md border border-border/50 bg-muted/40 px-2 py-1 text-xs"
                    >
                      {String(v)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Definition
                </Label>
                <Textarea
                  data-testid={GLOSSARY.drawerDefinition}
                  value={definition}
                  onChange={(e) => setDefinition(e.target.value)}
                  rows={4}
                  className="bg-card/60 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Lifecycle
                  </Label>
                  <Select value={lifecycle} onValueChange={setLifecycle}>
                    <SelectTrigger data-testid={GLOSSARY.drawerLifecycle}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LIFECYCLES.map((l) => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Sensitivity
                  </Label>
                  <Select value={sensitivity} onValueChange={setSensitivity}>
                    <SelectTrigger data-testid={GLOSSARY.drawerSensitivity}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SENSITIVITIES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Synonyms (comma-separated)
                </Label>
                <Input
                  data-testid={GLOSSARY.drawerSynonyms}
                  value={synonyms}
                  onChange={(e) => setSynonyms(e.target.value)}
                  placeholder="revenue, net sales"
                  className="bg-card/60 text-sm"
                />
              </div>

              <Button
                onClick={onSave}
                disabled={saving}
                data-testid={GLOSSARY.drawerSave}
                className="w-full rounded-full bg-accent text-xs uppercase tracking-[0.18em] text-accent-foreground hover:bg-accent/90"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
